import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import { useApi } from "../../hooks/useApi";
import { getServerStats } from "../../api/serverService";
import { getSettings, updateSettings } from "../../api/settingsService";
import { formatBytes } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

// Firm-facing "Usage & Limits": this firm's storage pool, seats, and the
// upload caps that govern what they can send. Whole-server metrics live in
// the super-admin Server page.
export default function ServerPage() {
  const stats = useApi(() => getServerStats(), []);
  const data = stats.data?.data || {};
  const storage = data.storage || {};
  const drive = data.drive || {};
  const seats = data.seats || {};
  const uploads = data.uploads || {};

  const storagePct = storage.quota_bytes ? Math.min(100, (storage.used_bytes / storage.quota_bytes) * 100) : 0;
  const storageTone = storagePct >= 90 ? "danger" : storagePct >= 75 ? "warning" : "ok";
  const seatPct = seats.total ? Math.min(100, (seats.used / seats.total) * 100) : 0;
  const seatTone = seatPct >= 100 ? "danger" : seatPct >= 80 ? "warning" : "ok";

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Usage &amp; Limits</h1>
          <div className="page-sub">Your firm's storage, seats, and upload limits</div>
        </div>
        <div className="row gap-sm">
          <button className="btn btn-secondary" onClick={() => stats.reload()} disabled={stats.loading}>
            <Icon name="refresh" size={13} /> {stats.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {stats.loading && !stats.data ? (
        <LoadingBlock rows={4} height={80} />
      ) : (
        <div className="col gap-md">
          <SettingsCard />
          <ChatFileRetentionCard />

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <div className="row gap-sm">
                  <IconBadge name="folder" color="info" />
                  <div>
                    <div className="card-title">Storage</div>
                    <div className="card-sub">Org drive + all client drives (shared pool)</div>
                  </div>
                </div>
              </div>
              <div className="card-body col gap-md">
                <UsageBar pct={storagePct} tone={storageTone} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
                  <Stat label="Used" value={formatBytes(storage.used_bytes)} />
                  <Stat label="Quota" value={`${storage.quota_gb ?? 0} GB`} />
                  <Stat label="Files" value={(drive.file_count ?? 0).toLocaleString()} />
                </div>
                {storageTone === "danger" && (
                  <div className="text-xs" style={{ color: "var(--danger)" }}>
                    Storage pool is nearly full — uploads will be blocked once it's exhausted. Contact Devsoleil for more space.
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <div className="row gap-sm">
                  <IconBadge name="users" color="primary" />
                  <div>
                    <div className="card-title">Seats</div>
                    <div className="card-sub">Active employees + admins beyond the first</div>
                  </div>
                </div>
              </div>
              <div className="card-body col gap-md">
                <UsageBar pct={seatPct} tone={seatTone} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
                  <Stat label="Used" value={seats.used ?? "—"} />
                  <Stat label="Paid seats" value={seats.total ?? "—"} highlight={seatTone} />
                </div>
                {seatTone === "danger" && (
                  <div className="text-xs" style={{ color: "var(--danger)" }}>
                    All seats are in use — you can't add members until Smart CA increases your seat count.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="row gap-sm">
                <IconBadge name="upload" color="warning" />
                <div>
                  <div className="card-title">Upload limits</div>
                  <div className="card-sub">Caps on a single upload request</div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <KeyVal label="Max file size" value={uploads.upload_max_filesize || "—"} />
              <KeyVal label="Max request size" value={uploads.post_max_size || "—"} />
              <KeyVal label="Max files per upload" value={uploads.max_file_uploads ?? "—"} />
              <div className="text-xs t-muted" style={{ marginTop: 8 }}>
                A batch upload must fit under the max request size. Large batches can exceed it even if each file is small — upload in smaller groups.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SettingsCard() {
  const settings = useApi(() => getSettings(), []);
  const [inherit, setInherit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const v = settings.data?.data?.recurring_inherit_assignee;
    if (typeof v === "boolean") setInherit(v);
  }, [settings.data]);

  const toggle = async (next) => {
    setInherit(next);
    setSaving(true);
    try {
      await updateSettings({ recurring_inherit_assignee: next });
      showToast("success", next ? "Inheritance enabled" : "Inheritance disabled");
    } catch (err) {
      setInherit(!next);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="row gap-sm">
          <IconBadge name="settings" color="primary" />
          <div>
            <div className="card-title">Task generation</div>
            <div className="card-sub">How new recurring task instances are created</div>
          </div>
        </div>
      </div>
      <div className="card-body">
        <label className="row gap-sm" style={{ alignItems: "flex-start", cursor: saving ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={inherit}
            disabled={saving || settings.loading}
            onChange={(e) => toggle(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ flex: 1 }}>
            <span className="text-sm fw-600 t-primary">Carry assignee &amp; reviewer to new recurring instances</span>
            <span className="text-xs t-muted" style={{ display: "block", marginTop: 2 }}>
              New occurrences reuse the last admin-set assignee and reviewer for the same client + task
              (anyone who has left is skipped). When off, instances are created unassigned.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

// Firm admin controls how long chat file attachments are kept before the daily
// prune deletes them. Default is Never (files kept indefinitely). Only shown
// when the firm has the chat addon.
function ChatFileRetentionCard() {
  const settings = useApi(() => getSettings(), []);
  const [never, setNever] = useState(true);
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const data = settings.data?.data;

  useEffect(() => {
    if (!data) return;
    const v = data.chat_file_retention_days;
    if (v === null || v === undefined) {
      setNever(true);
    } else {
      setNever(false);
      setDays(v);
    }
  }, [data]);

  // Wait for the first load; only relevant when chat is enabled.
  if (!data) return null;
  if (!data.chat_enabled) return null;

  const persist = async (payload, optimistic) => {
    optimistic?.();
    setSaving(true);
    try {
      await updateSettings(payload);
      showToast("success", "Chat file retention updated");
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleNever = (checked) => {
    persist({ chat_file_retention_days: checked ? null : Math.max(1, days) }, () => setNever(checked));
  };

  const saveDays = () => {
    if (never) return;
    const n = Math.min(3650, Math.max(1, parseInt(days, 10) || 1));
    setDays(n);
    persist({ chat_file_retention_days: n });
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="row gap-sm">
          <IconBadge name="paperclip" color="info" />
          <div>
            <div className="card-title">Chat file retention</div>
            <div className="card-sub">How long shared files are kept in chat</div>
          </div>
        </div>
      </div>
      <div className="card-body col gap-sm">
        <label className="row gap-sm" style={{ alignItems: "center", cursor: saving ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={never}
            disabled={saving}
            onChange={(e) => toggleNever(e.target.checked)}
          />
          <span className="text-sm fw-600 t-primary">Never delete (keep files forever)</span>
        </label>

        {!never && (
          <div className="row gap-sm" style={{ alignItems: "center" }}>
            <span className="text-sm t-muted">Delete files after</span>
            <input
              type="number"
              min={1}
              max={3650}
              className="input"
              style={{ width: 90 }}
              value={days}
              disabled={saving}
              onChange={(e) => setDays(e.target.value)}
              onBlur={saveDays}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveDays(); } }}
            />
            <span className="text-sm t-muted">days</span>
          </div>
        )}

        <div className="text-xs t-muted">
          Files are kept forever unless you set a limit. Shared files count toward your storage pool, so a shorter window frees space sooner.
        </div>
      </div>
    </div>
  );
}

function IconBadge({ name, color }) {
  const map = {
    primary: { bg: "var(--primary-50)", fg: "var(--primary-700)" },
    info: { bg: "var(--info-100)", fg: "var(--info)" },
    warning: { bg: "var(--warning-100)", fg: "var(--warning)" },
    success: { bg: "var(--success-100)", fg: "var(--success)" },
  };
  const c = map[color] || map.primary;
  return (
    <div style={{ width: 32, height: 32, borderRadius: 8, background: c.bg, color: c.fg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon name={name} size={16} />
    </div>
  );
}

function UsageBar({ pct, tone }) {
  const colorMap = { ok: "var(--success)", warning: "var(--warning)", danger: "var(--danger)" };
  return (
    <div style={{ height: 10, borderRadius: 6, background: "var(--surface-2)", overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: colorMap[tone] || colorMap.ok, transition: "width .25s" }} />
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const color = highlight === "danger" ? "var(--danger)" : highlight === "warning" ? "var(--warning)" : "var(--ink-900)";
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, letterSpacing: -0.5, marginTop: 2 }}>{value ?? "—"}</div>
    </div>
  );
}

function KeyVal({ label, value }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed var(--border)" }}>
      <span className="t-muted text-xs">{label}</span>
      <span className="text-sm fw-600">{value}</span>
    </div>
  );
}
